




















































































































import _ from 'lodash';
import {
  computed,
  defineComponent,
  nextTick,
  Ref,
  ref,
  getCurrentInstance,
  ComponentInternalInstance,
  watch,
} from '@vue/composition-api';
import { DialogPlugin } from 'tdesign-vue';
import { HelpCircleIcon, CheckIcon } from 'tdesign-icons-vue';
import i18n, { i18n_t } from '@/locales/i18n';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips, { MsgError } from '@/renderer/scripts/utils/tips';
import { isDisabledAllAuth } from '@/renderer/scripts/utils/tools/index';
import CustomDialog from '@/renderer/scripts/utils/CustomDialog';

interface PropData {
  show: boolean;
  modifyData: SMH.CustomRole;
  showType: 'add' | 'modify' | string;
}
export default defineComponent({
  name: 'AddRole',
  components: {
    HelpCircleIcon,
    CheckIcon,
  },
  props: {
    show: {
      required: true,
      type: Boolean,
      default: false,
    },
    modifyData: Object,
    showType: {
      required: true,
      type: String,
      default: 'add',
    },
  },
  setup(props: PropData, context) {
    const innerShow = computed({
      get() {
        return props.show;
      },
      set(val) {
        context.emit('update:show', val);
      },
    });
    const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
    const vm = currComponent.proxy;
    const store = vm.$store;
    const columns = [
      {
        colKey: 'canView',
        title: i18n_t('查看'),
        width: 75,
        align: 'center',
      },
      {
        colKey: 'canPreview',
        title: i18n_t('预览'),
        width: 75,
        align: 'center',
      },
      {
        colKey: 'canDownload',
        title: i18n_t('下载'),
        width: 75,
        align: 'center',
      },
      {
        colKey: 'canUpload',
        title: i18n_t('上传'),
        width: 75,
        align: 'center',
      },
      {
        colKey: 'canPrint',
        title: i18n_t('打印'),
        width: 75,
        align: 'center',
      },
      {
        colKey: 'canDelete',
        title: i18n_t('删除'),
        width: 75,
        align: 'center',
      },
      {
        colKey: 'canModify',
        title: i18n_t('修改'),
        width: 75,
        align: 'center',
      },
      {
        colKey: 'canAuthorize',
        title: i18n_t('共享'),
        width: 75,
        align: 'center',
      },
      {
        colKey: 'canShare',
        title: i18n_t('分享'),
        width: 75,
        align: 'center',
      },
    ];
    const combineRolesOptions = [
      {
        name: i18n_t('新建目录/文档'),
        require: [i18n_t('查看'), i18n_t('上传')],
      },
      {
        name: i18n_t('复制'),
        require: [i18n_t('查看'), i18n_t('下载'), i18n_t('上传')],
      },
      {
        name: i18n_t('移动'),
        require: [i18n_t('查看'), i18n_t('下载'), i18n_t('上传'), i18n_t('删除')],
      },
      {
        name: i18n_t('重命名'),
        require: [i18n_t('查看'), i18n_t('下载'), i18n_t('上传'), i18n_t('删除')],
      },
    ];
    const nameRules = [
      {
        required: true,
        trigger: 'blur',
        message: i18n_t('名称不能为空'),
        type: 'error',
      },
      {
        whitespace: true,
        message: i18n_t('名称不能为空'),
      },
      {
        max: 20,
        message: i18n_t('名称长度需控制在 20 字符以内'),
        type: 'error',
      },
    ];
    const roleData = ref<
      SMH.CustomRole & {
        isCustom?: boolean;
      }
    >({
      name: '',
      canView: true,
      canPreview: false,
      canDownload: false,
      canUpload: false,
      canPrint: false,
      canDelete: false,
      canModify: false,
      canAuthorize: false,
      canShare: false,
      canPreviewSelf: false,
      canDownloadSelf: false,
      roleDesc: '',
    });
    const disableSubmit = ref(false);
    const showDeleteConfirm = ref(false);
    const replaceRoleId = ref(1);
    const roleList = computed<SMH.Role[]>(() => vm.$store.getters.roles);
    const replaceOption = computed(() => {
      const options = roleList.value.filter((role: SMH.Role) => !role.isCustom && !isDisabledAllAuth(role));
      replaceRoleId.value = options.length > 0 ? options[0].id : 1;
      return options.map(role => ({
        label: role.name,
        value: role.id,
      }));
    });
    const isModified = computed(() => props.showType === 'modify');
    const combineRoles = computed(() => {
      const allow = {
        /* i18n-ignore*/
        '新建目录/文档': roleData.value.canUpload,
        /* i18n-ignore*/
        复制: roleData.value.canUpload && roleData.value.canDownload,
        /* i18n-ignore*/
        移动: roleData.value.canDownload && roleData.value.canUpload && roleData.value.canDelete,
        /* i18n-ignore*/
        重命名: roleData.value.canDownload && roleData.value.canUpload && roleData.value.canDelete,
      };
      return allow;
    });
    const canPrint = computed({
      get() {
        return roleData.value.canPrint;
      },
      set(val: boolean) {
        if (val) {
          roleData.value.canPreview = true;
          roleData.value.canModify = true;
          roleData.value.canDownload = true;
          roleData.value.canPrint = true;
        }
        roleData.value.canPrint = val;
      },
    });
    watch(
      [() => roleData.value.canPreview, () => roleData.value.canDownload, () => roleData.value.canModify],
      ([canPreview, canDownload, canModify]) => {
        // 取消下载 或 预览/编辑其中之一被取消，则打印按钮不选中
        if (!canDownload || !(canPreview || canModify)) {
          canPrint.value = false;
        }
      },
    );
    const methods = {
      submit() {
        if (disableSubmit.value) return;
        roleData.value.name = roleData.value.name.trim();
        const sameWithExisting = methods.diffExistRole(roleData.value);
        if (sameWithExisting) return;
        methods.updateRoleList();
        innerShow.value = false;
      },
      combineAuthStr() {
        const combineStr: string[] = [];
        Object.keys(roleData.value).forEach(key => {
          if (key.startsWith('can') && roleData.value[key as keyof SMH.CustomRole]) {
            const item = columns.find(column => column.colKey === key);
            item && combineStr.push(item.title);
          }
        });
        return combineStr;
      },
      async updateRoleList() {
        try {
          let listData;
          if (isModified.value) {
            await smh.userAPI.updateRoleList(null, {
              body: [roleData.value],
            });
            listData = roleList.value.map(item => (item.name === props.modifyData.name ? roleData.value : item));
          } else {
            roleData.value.isCustom = true;
            const { data: roles } = await smh.userAPI.createRoleList(null, {
              body: [roleData.value],
            });
            listData = [...roleList.value, ...roles];
          }
          store.commit('SET_ROLES', listData);
          tips.success(isModified.value ? i18n_t('编辑成功') : i18n_t('新增成功'));
          reporter.report('authorization', {
            set_define: methods.combineAuthStr(),
          });
        } catch (e) {
          tips.error(e as MsgError);
        }
      },
      handleDeleteRole() {
        showDeleteConfirm.value = true;
      },
      async deleteRole() {
        try {
          const deleteData = {
            roleIds: [roleData.value.id as number],
            replaceRoleId: replaceRoleId.value,
          };
          await smh.userAPI.deleteRoleList(deleteData);
          const deleteListData = _.remove(roleList.value, ele => ele.id !== roleData.value.id);
          store.commit('SET_ROLES', deleteListData);
          showDeleteConfirm.value = false;
          innerShow.value = false;
          tips.success(i18n_t('删除成功'));
          methods.checkIfRefreshOrgInfo();
        } catch (e) {
          tips.error(e as MsgError);
        }
      },
      /**
       * 检查是否要刷新企业信息
       */
      checkIfRefreshOrgInfo() {
        if (roleData.value.id === store.getters.defaultTeamOptions.defaultRoleId) {
          // 删除角色若为团队默认权限，则需要刷新当前页面显示
          store.dispatch('GetOrgInfo', store.getters.orgId);
        }
      },
      diffExistRole(newRole: SMH.CustomRole) {
        const oldName = isModified.value ? props.modifyData.name : '';
        let sameName = '';
        const keys: Array<keyof SMH.CustomRole> = [
          'canView',
          'canPreview',
          'canDownload',
          'canUpload',
          'canPrint',
          'canDelete',
          'canModify',
          'canAuthorize',
          'canShare',
        ];
        for (let i = 0; i < roleList.value.length; i++) {
          let sameRole = true;
          const role = roleList.value[i];
          // eslint-disable-next-line no-loop-func
          keys.forEach(key => {
            if (role[key] !== newRole[key]) {
              sameRole = false;
            }
          });
          if (sameRole && oldName !== role.name) {
            sameName = role.name;
            break;
          }
        }
        if (sameName) {
          const dialog = CustomDialog.confirm({
            header: i18n_t('创建权限角色失败'),
            body: i18n_t('当前设置权限与【{sameName}】权限相同，请重新设置', {
              sameName,
            }),
            theme: 'warning',
            onConfirm: () => {
              if (dialog.hide) {
                dialog.hide();
              }
            },
          });
        }
        return !!sameName;
      },
      validateName: _.throttle(() => {
        (vm.$refs.form as any).validate().then((res: boolean) => {
          disableSubmit.value = typeof res !== 'boolean';
        });
      }, 500),
    };
    watch(innerShow, (value: Ref<unknown>) => {
      if (value) {
        if (props.showType === 'modify') {
          nextTick(() => {
            roleData.value = _.cloneDeep(props.modifyData);
          });
        } else {
          roleData.value = {
            name: '',
            canView: true,
            canPreview: false,
            canDownload: false,
            canUpload: false,
            canPrint: false,
            canDelete: false,
            canModify: false,
            canAuthorize: false,
            canShare: false,
            canPreviewSelf: false,
            canDownloadSelf: false,
            roleDesc: '',
          };
          nextTick(() => {
            const { createFolderInput }: any = (currComponent as ComponentInternalInstance).refs;
            const inputDom = createFolderInput?.$el.children[0]?.children[0] as HTMLInputElement;
            inputDom && inputDom?.select();
          });
        }
      } else {
        disableSubmit.value = false;
        (vm.$refs.form as any)?.clearValidate();
      }
    });
    return {
      innerShow,
      columns,
      nameRules,
      combineRolesOptions,
      combineRoles,
      roleData,
      canPrint,
      disableSubmit,
      showDeleteConfirm,
      replaceRoleId,
      replaceOption,
      ...methods,
    };
  },
});
